import { createElement, type ComponentType } from 'react'
import { createLazyComponent, type TLazyComponent } from '@muse-web/router'

import { useI18n } from '@muse/i18n'
import { toast } from '@ds/components'

type TDialogProps = {
    open: boolean
    onOpenChange: (open: boolean) => void
}

type TLoadFailureProps = {
    onLoadFailure: () => void
}

export type TLazyDialog<TProps extends TDialogProps> = TLazyComponent<TProps>

const noop = () => undefined

export const createLazyDialog = <TProps extends TDialogProps, TName extends string>(
    load: () => Promise<Record<TName, ComponentType<TProps>>>,
    name: TName,
): TLazyDialog<TProps> => {
    const loadDialog = () =>
        load().then(module => {
            const Dialog: ComponentType<TProps> = module[name]
            const LoadedDialog = (props: TProps) => createElement(Dialog, props)

            return { LoadedDialog }
        })

    const RouterDialog = createLazyComponent<TProps & TLoadFailureProps, 'LoadedDialog'>(
        loadDialog,
        'LoadedDialog',
        {
            loadWhen: ({ open }) => open,
            onLoadError: (_, { onLoadFailure }) => onLoadFailure(),
        },
    )

    const LazyDialog = (props: TProps) => {
        const { onOpenChange } = props
        const { t } = useI18n()

        const handleLoadFailure = () => {
            toast.error(t('i18n.commonSomethingWentWrong')).catch(noop)
            onOpenChange(false)
        }

        return <RouterDialog {...props} onLoadFailure={handleLoadFailure} />
    }

    return Object.assign(LazyDialog, { preload: RouterDialog.preload })
}
